import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-semibold transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:transition-transform [&_svg]:duration-200 active:scale-[0.97] active:opacity-90 hover:brightness-105 ripple-effect tap-highlight",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground rounded-xl shadow-ios-sm btn-glow hover:-translate-y-0.5",
        destructive: "bg-destructive text-destructive-foreground rounded-xl hover:-translate-y-0.5",
        outline: "border border-border bg-transparent rounded-xl hover:bg-secondary/30 hover:-translate-y-0.5",
        secondary: "bg-secondary text-secondary-foreground rounded-xl hover:-translate-y-0.5",
        ghost: "rounded-xl hover:bg-secondary/50",
        link: "text-primary underline-offset-4 hover:underline",
        glow: "bg-primary text-primary-foreground rounded-xl btn-glow btn-shine hover:-translate-y-0.5",
      },
      size: {
        default: "h-11 px-6 py-2",
        sm: "h-9 rounded-lg px-4 text-xs",
        lg: "h-12 rounded-xl px-8",
        icon: "h-10 w-10 rounded-full pulse-ring-hover",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
